// frontend/src/modules/invoicing/components/InvoiceLineItemsEditor.tsx
import * as React from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { ServicePicker } from './ServicePicker';
import { formatMoney } from '../../../design-system/format';
import { computeTotals, type InvoiceDiscount } from '../totals';
import type { CreateInvoiceItemInput, Service } from '../types';
import { Trash2 } from 'lucide-react';

// Numeric grid cells are far narrower than the design-system pill's default
// px-4 + native number spinners allow — compact padding + spinner removal so
// 2-4 digit values stay visible instead of clipping into an empty oval.
const NUMERIC_CELL =
  'text-right px-2 tabular-nums [appearance:textfield] ' +
  '[&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none';

interface Props {
  items: CreateInvoiceItemInput[];
  onChange: (next: CreateInvoiceItemInput[]) => void;
  currency: string;
  /** Invoice-level discount (036) — folded into the totals footer. */
  invoiceDiscount?: InvoiceDiscount | null;
  /** Tax-profile rate override (036) — replaces every line's tax rate. */
  taxRateOverride?: number | null;
}

export function InvoiceLineItemsEditor({ items, onChange, currency, invoiceDiscount, taxRateOverride }: Props) {
  function update(idx: number, patch: Partial<CreateInvoiceItemInput>) {
    onChange(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  }
  function add() {
    onChange([...items, { description: '', quantity: 1, unit_price: 0, tax_rate: 0 }]);
  }
  function remove(idx: number) {
    if (items.length === 1) return; // keep at least one row
    onChange(items.filter((_, i) => i !== idx));
  }
  function pickService(idx: number, s: Service | null) {
    if (!s) { update(idx, { service_id: null }); return; }
    update(idx, {
      service_id: s.id,
      description: s.description || s.name,
      unit_price: Number(s.default_unit_price),
      tax_rate: Number(s.default_tax_rate),
    });
  }

  // Shared money math — mirrors the server exactly (server stays canonical).
  const totals = computeTotals(items, { invoiceDiscount, taxRateOverride });
  const taxOverridden = taxRateOverride != null;

  return (
    <div className="border rounded-md">
      <div className="grid grid-cols-12 gap-2 px-3 py-2 border-b text-xs font-medium text-muted-foreground">
        <div className="col-span-2 whitespace-nowrap">Service</div>
        <div className="col-span-3">Description</div>
        <div className="col-span-1 text-right">Qty</div>
        <div className="col-span-2 text-right whitespace-nowrap">Unit price</div>
        <div className="col-span-1 text-right whitespace-nowrap">Disc %</div>
        <div className="col-span-1 text-right whitespace-nowrap">Tax %</div>
        <div className="col-span-2 text-right whitespace-nowrap">Line total</div>
      </div>
      {items.map((it, idx) => {
        const lineWithTax = (totals.lineSubtotals[idx] ?? 0) + (totals.lineTaxes[idx] ?? 0);
        return (
          <div key={idx} className="grid grid-cols-12 gap-2 px-3 py-2 border-b last:border-b-0 items-center">
            <div className="col-span-2 min-w-0">
              <ServicePicker value={it.service_id ?? null} onChange={(s) => pickService(idx, s)} />
            </div>
            <div className="col-span-3 min-w-0">
              <Input value={it.description} onChange={(e) => update(idx, { description: e.target.value })} placeholder="Description" />
            </div>
            <div className="col-span-1 min-w-0">
              <Input type="number" min="0" step="0.01" className={NUMERIC_CELL} value={it.quantity}
                onChange={(e) => update(idx, { quantity: Number(e.target.value) })} />
            </div>
            <div className="col-span-2 min-w-0">
              <Input type="number" min="0" step="0.01" className={`${NUMERIC_CELL} font-mono`} value={it.unit_price}
                onChange={(e) => update(idx, { unit_price: Number(e.target.value) })} />
            </div>
            <div className="col-span-1 min-w-0">
              <Input type="number" min="0" step="0.01" max="100" className={NUMERIC_CELL} value={it.discount_percent ?? ''}
                placeholder="—"
                onChange={(e) => update(idx, { discount_percent: e.target.value === '' ? null : Number(e.target.value) })} />
            </div>
            <div className="col-span-1 min-w-0">
              <Input type="number" min="0" step="0.01" max="100" className={NUMERIC_CELL}
                value={taxOverridden ? taxRateOverride! : (it.tax_rate ?? 0)}
                disabled={taxOverridden}
                title={taxOverridden ? 'Tax profile overrides line rates' : undefined}
                onChange={(e) => update(idx, { tax_rate: Number(e.target.value) })} />
            </div>
            <div className="col-span-2 min-w-0 text-right font-mono text-sm flex items-center justify-end gap-2 whitespace-nowrap tabular-nums">
              <span className="truncate">{formatMoney(lineWithTax, currency)}</span>
              <button type="button" onClick={() => remove(idx)} className="shrink-0 text-muted-foreground hover:text-red-600 dark:hover:text-red-400">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        );
      })}
      <div className="px-3 py-2 border-t flex items-center justify-between">
        <Button type="button" variant="outline" size="sm" onClick={add}>+ Add line</Button>
        <div className="text-right text-sm space-y-0.5">
          <div className="flex justify-between gap-12">
            <span className="text-muted-foreground">Subtotal</span>
            <span className="font-mono">{formatMoney(totals.subtotal, currency)}</span>
          </div>
          {totals.discountTotal > 0 && (
            <div className="flex justify-between gap-12">
              <span className="text-muted-foreground">Discount</span>
              <span className="font-mono">-{formatMoney(totals.discountTotal, currency)}</span>
            </div>
          )}
          <div className="flex justify-between gap-12">
            <span className="text-muted-foreground">Tax</span>
            <span className="font-mono">{formatMoney(totals.taxTotal, currency)}</span>
          </div>
          <div className="flex justify-between gap-12 font-medium text-base">
            <span>Total</span>
            <span className="font-mono">{formatMoney(totals.total, currency)}</span>
          </div>
          <div className="text-xs text-muted-foreground">Server recomputes on save</div>
        </div>
      </div>
    </div>
  );
}
